import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getOrderFieldServiceKeys, isOrderBaseColumn } from '../config/order-columns-config';
import {
  ORDER_SERVICE_ACCESS_KEYS,
  getOrderServiceAccessPermissionName,
} from '../config/order-service-access';

export interface PermissionHookResult {
  hasPageAccess: (pageKey: string) => boolean;
  hasOrderServiceAccess: (serviceKey?: string) => boolean;
  getAccessibleOrderServices: () => string[];
  hasColumnAccess: (pageKey: string, columnKey: string) => boolean;
  hasActionAccess: (pageKey: string, actionKey: string) => boolean;
  hasFormFieldAccess: (pageKey: string, fieldKey: string, type: 'view' | 'edit', serviceKey?: string) => boolean;
  getVisibleColumns: (pageKey: string, allColumns: any[]) => any[];
  getVisibleActions: (pageKey: string, allActions: string[]) => string[];
  getEditableFormFields: (pageKey: string, allFields: string[]) => string[];
  filterFormFields: (pageKey: string, formFields: any[], type: 'view' | 'edit') => any[];
}

export const usePermissions = (): PermissionHookResult => {
  const { hasPermission, hasRole, state } = useAuth();

  // 获取用户权限列表
  const userPermissions = useMemo(() => {
    return state.permissions || [];
  }, [state.permissions]);

  // 获取用户角色列表
  const userRoles = useMemo(() => {
    return state.roles || [];
  }, [state.roles]);

  const columnPermissionAliases = useMemo(() => ({
    'follow-up-management': {
      'customer_categories': ['client_type'],
    },
    'trademark-monitor': {
      'trademark_name': ['name'],
      'owner_username': ['user_id'],
    },
  } as Record<string, Record<string, string[]>>), []);

  // 子页面权限继承映射
  const pageInheritanceMap = useMemo(() => ({
    'data-settings': 'system', // 数据设置继承系统管理权限
    'schedule-tasks': 'system', // 定时任务继承系统管理权限
    'notifications': 'system', // 通知模板继承系统管理权限
    'trademark-monitor': 'order-management',
    'patent-payment-management': 'order-management',
  } as Record<string, string | string[]>), []);

  const hasConfiguredOrderServiceAccess = useMemo(() => {
    return userPermissions.some(permission => /^page\.order-management\.service\.[^.]+\.view$/.test(permission));
  }, [userPermissions]);

  const hasPageAccess = (pageKey: string): boolean => {
    // 管理员（admin）始终有权限
    if (hasRole('admin')) {
      return true;
    }

    // 检查直接页面访问权限
    if (hasPermission(`page.${pageKey}.view`)) {
      return true;
    }

    // 检查是否为子页面，如果是则检查父页面权限
    const parentPages = pageInheritanceMap[pageKey];
    if (parentPages) {
      if (Array.isArray(parentPages)) {
        // 如果有多个父页面，只要有一个有权限即可
        return parentPages.some(parentPage => {
          // 检查 page.{parentPage}.view 或者 {parentPage}.view 格式
          return hasPermission(`page.${parentPage}.view`) || hasPermission(`${parentPage}.view`);
        });
      } else {
        // 单个父页面
        return hasPermission(`page.${parentPages}.view`) || hasPermission(`${parentPages}.view`);
      }
    }

    return false;
  };

  const hasOrderServiceAccess = (serviceKey: string = 'all'): boolean => {
    if (hasRole('admin')) {
      return true;
    }

    if (!hasPageAccess('order-management')) {
      return false;
    }

    // 兼容旧角色：尚未配置任何业务订单子菜单权限时，仍按页面访问权限放行。
    if (!hasConfiguredOrderServiceAccess) {
      return true;
    }

    if (serviceKey !== 'all' && hasPermission(getOrderServiceAccessPermissionName('all'))) {
      return true;
    }

    return hasPermission(getOrderServiceAccessPermissionName(serviceKey || 'all'));
  };

  const getAccessibleOrderServices = (): string[] => {
    if (hasRole('admin') || !hasConfiguredOrderServiceAccess) {
      return ORDER_SERVICE_ACCESS_KEYS;
    }

    return ORDER_SERVICE_ACCESS_KEYS.filter(serviceKey => hasOrderServiceAccess(serviceKey));
  };

  const hasColumnAccess = (pageKey: string, columnKey: string): boolean => {
    // 管理员（admin）始终有权限
    if (hasRole('admin')) {
      return true;
    }

    // 检查直接列权限
    if (hasPermission(`page.${pageKey}.column.${columnKey}`) ||
        hasPermission(`page.${pageKey}.column.*`)) {
      return true;
    }

    // 检查是否为子页面，如果是则检查父页面权限
    const parentPages = pageInheritanceMap[pageKey];
    if (parentPages) {
      if (Array.isArray(parentPages)) {
        return parentPages.some(parentPage => {
          return hasPermission(`page.${parentPage}.column.${columnKey}`) ||
                 hasPermission(`${parentPage}.column.${columnKey}`);
        });
      } else {
        return hasPermission(`page.${parentPages}.column.${columnKey}`) ||
               hasPermission(`${parentPages}.column.${columnKey}`);
      }
    }

    return false;
  };

  const hasOrderColumnAccess = (columnKey: string, serviceKey?: string): boolean => {
    if (hasRole('admin')) {
      return true;
    }

    if (isOrderBaseColumn(columnKey)) {
      return hasColumnAccess('order-management', columnKey);
    }

    const normalizedServiceKey = serviceKey === 'madrid' ? 'trademark' : serviceKey;
    const serviceKeys = normalizedServiceKey && normalizedServiceKey !== 'all'
      ? [normalizedServiceKey]
      : getOrderFieldServiceKeys(columnKey);

    if (serviceKeys.length === 0) {
      return hasColumnAccess('order-management', columnKey);
    }

    return serviceKeys.some(service => hasPermission(`page.order-management.column.${service}.${columnKey}`));
  };

  const hasActionAccess = (pageKey: string, actionKey: string): boolean => {
    // 管理员（admin）始终有权限
    if (hasRole('admin')) {
      return true;
    }

    // 检查直接操作权限
    if (hasPermission(`page.${pageKey}.action.${actionKey}`)) {
      return true;
    }

    // 操作权限必须按当前页面独立授权，避免父页面同名动作误放行子页面按钮。
    return false;
  };

  const hasFormFieldAccess = (pageKey: string, fieldKey: string, type: 'view' | 'edit', serviceKey?: string): boolean => {
    // 管理员（admin）始终有权限
    if (hasRole('admin')) {
      return true;
    }

    if (pageKey === 'order-management' && !isOrderBaseColumn(fieldKey)) {
      const normalizedServiceKey = serviceKey === 'madrid' ? 'trademark' : serviceKey;
      const serviceKeys = normalizedServiceKey && normalizedServiceKey !== 'all'
        ? [normalizedServiceKey]
        : getOrderFieldServiceKeys(fieldKey);

      if (serviceKeys.length > 0) {
        return serviceKeys.some(service => {
          const direct = hasPermission(`page.${pageKey}.form.${service}.${fieldKey}.${type}`);
          const editImpliesView = type === 'view' && hasPermission(`page.${pageKey}.form.${service}.${fieldKey}.edit`);

          return direct || editImpliesView;
        });
      }
    }

    // 检查直接表单字段权限
    if (hasPermission(`page.${pageKey}.form.${fieldKey}.${type}`)) {
      return true;
    }

    // 如果请求查看权限，但用户有编辑权限，也应该允许查看
    if (type === 'view' && hasPermission(`page.${pageKey}.form.${fieldKey}.edit`)) {
      return true;
    }

    // 检查是否为子页面，如果是则检查父页面权限
    const parentPages = pageInheritanceMap[pageKey];
    if (parentPages) {
      if (Array.isArray(parentPages)) {
        return parentPages.some(parentPage => {
          const hasDirectPermission = hasPermission(`page.${parentPage}.form.${fieldKey}.${type}`) ||
                                     hasPermission(`${parentPage}.form.${fieldKey}.${type}`);

          // 如果请求查看权限，检查是否有编辑权限
          const hasEditPermission = type === 'view' && (
            hasPermission(`page.${parentPage}.form.${fieldKey}.edit`) ||
            hasPermission(`${parentPage}.form.${fieldKey}.edit`)
          );

          return hasDirectPermission || hasEditPermission;
        });
      } else {
        const hasDirectPermission = hasPermission(`page.${parentPages}.form.${fieldKey}.${type}`) ||
                                   hasPermission(`${parentPages}.form.${fieldKey}.${type}`);

        // 如果请求查看权限，检查是否有编辑权限
        const hasEditPermission = type === 'view' && (
          hasPermission(`page.${parentPages}.form.${fieldKey}.edit`) ||
          hasPermission(`${parentPages}.form.${fieldKey}.edit`)
        );

        return hasDirectPermission || hasEditPermission;
      }
    }

    return false;
  };

  const getVisibleColumns = (pageKey: string, allColumns: any[]): any[] => {
    return allColumns.filter(column => {
      const permissionKey = (column as any)?.permissionKey || column.key;
      const fallbackKey = column.key;
      const aliasKeys = columnPermissionAliases[pageKey]?.[permissionKey] || [];
      const keysToCheck = Array.from(new Set([permissionKey, fallbackKey, ...aliasKeys]));

      // actions 列特殊处理：如果用户有任何操作权限，则显示 actions 列
      if (fallbackKey === 'actions') {
        return userPermissions.some(permission => permission.startsWith(`page.${pageKey}.action.`)) ||
               hasRole('admin');
      }

      // 默认可见列不需要权限检查
      const defaultVisibleColumns = ['id', 'created_at', 'updated_at'];
      if (keysToCheck.some(key => defaultVisibleColumns.includes(key))) {
        return true;
      }

      if (pageKey === 'order-management') {
        return keysToCheck.some(key => hasOrderColumnAccess(key, (column as any)?.permissionService));
      }

      return keysToCheck.some(key => hasColumnAccess(pageKey, key));
    });
  };

  const getVisibleActions = (pageKey: string, allActions: string[]): string[] => {
    return allActions.filter(action => hasActionAccess(pageKey, action));
  };

  const getEditableFormFields = (pageKey: string, allFields: string[]): string[] => {
    return allFields.filter(field => hasFormFieldAccess(pageKey, field, 'edit'));
  };

  const filterFormFields = (pageKey: string, formFields: any[], type: 'view' | 'edit'): any[] => {
    return formFields.filter(field => {
      const fieldKey = field.name || field.key;
      return hasFormFieldAccess(pageKey, fieldKey, type);
    });
  };

  return {
    hasPageAccess,
    hasOrderServiceAccess,
    getAccessibleOrderServices,
    hasColumnAccess,
    hasActionAccess,
    hasFormFieldAccess,
    getVisibleColumns,
    getVisibleActions,
    getEditableFormFields,
    filterFormFields
  };
};

export default usePermissions;
